import { useEffect, useState } from 'react';
import { COMMERCIAL_PRICING } from '@connexio/shared/constants';
import type { EnterpriseEnquiryInput, PublicCommercialConfig } from '@connexio/shared';
import { publicGet, publicPost } from '@/lib/publicFetch';

/**
 * Public marketing API without TanStack Query or axios, so the start page stays light
 * (CX-397). The app's own screens use `./api` (React Query).
 */

export const COMMERCIAL_CONFIG_FALLBACK: PublicCommercialConfig = {
  signupEnabled: false,
  trialDays: COMMERCIAL_PRICING.trialDays,
  trialRequiresCard: false,
  bankidEnabled: false,
  pricing: {
    currency: COMMERCIAL_PRICING.currency,
    interval: COMMERCIAL_PRICING.interval,
    unit: COMMERCIAL_PRICING.unit,
    amountExVatSek: COMMERCIAL_PRICING.amountExVatSek,
    vatRatePercent: COMMERCIAL_PRICING.vatRatePercent,
    amountIncVatSek: COMMERCIAL_PRICING.amountIncVatSek,
  },
};

export function fetchCommercialConfig(): Promise<PublicCommercialConfig> {
  return publicGet<PublicCommercialConfig>('/commercial-config');
}

export function submitEnterpriseEnquiry(input: EnterpriseEnquiryInput): Promise<{ received: true }> {
  return publicPost<{ received: true }>('/enterprise-enquiry', input);
}

const CONFIG_TTL_MS = 5 * 60 * 1000;
let cached: { value: PublicCommercialConfig; at: number } | null = null;
let inflight: Promise<PublicCommercialConfig> | null = null;

function loadConfig(): Promise<PublicCommercialConfig> {
  if (cached && Date.now() - cached.at < CONFIG_TTL_MS) return Promise.resolve(cached.value);
  inflight ??= fetchCommercialConfig()
    .then((value) => {
      cached = { value, at: Date.now() };
      return value;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Test helper: forget the cached config. */
export function resetCommercialConfigCache(): void {
  cached = null;
  inflight = null;
}

/** The commercial config with the code fallback until (or if) the request settles. */
export function useCommercialConfig(): { data: PublicCommercialConfig } {
  const [data, setData] = useState<PublicCommercialConfig>(cached?.value ?? COMMERCIAL_CONFIG_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    loadConfig()
      .then((value) => {
        if (!cancelled) setData(value);
      })
      .catch(() => {
        // Keep the fallback: the page works without the live config.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { data };
}
